import { HTTPResponseError } from "@validereinc/utilities";
import type { EventType } from "../schemas/EventSchemas.ts";
import type { FormSubmissionType } from "../schemas/FormSubmissionSchemas.ts";
import type { WorkflowType } from "../schemas/WorkflowSchemas.ts";
import type {
  WorkflowTaskBaseSchema,
  WorkflowTaskGetListRequestSchema,
  WorkflowTaskGetListRequestType,
} from "../schemas/WorkflowTaskSchemas.ts";
import { type WorkflowTaskType } from "../schemas/WorkflowTaskSchemas.ts";
import type {
  DeleteOneRequestType,
  FilterObjectType,
  GetListRequestType,
  GetListResponseType,
  ResourceAdapterType,
  ResponseErrorType,
  ResponseSuccessType,
  UpdateOneRequestType,
  UpdateOneResponseType,
} from "../util/index.ts";
import {
  fetchAndCollate,
  getFilterObject,
  isResponseErrorType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";
import type { SavedFiltersAdapterType } from "./composable/index.ts";
import { SavedFiltersAdapter } from "./composable/index.ts";

// This type is different from any other paginated responses we have in the app.
// This endpoint is primarily used by the mobile app:
type GetListResponseTypeOfWorkflowTasksWithRelations = Omit<
  GetListResponseType<unknown>,
  "data"
> & {
  tasks: WorkflowTaskBaseSchema[];
  workflows: WorkflowType[];
  workflow_forms: Array<{
    id: string;
    workflow_id: string;
    associated_tasks?: Array<{
      workflow_task_id: string;
    }>;
  }>;
  form_submissions: FormSubmissionType[];
  events: Array<Pick<EventType, "id" | "name">>;
  relations: {
    task_form_submissions: Array<{
      task_id: string;
      form_submission_id: string;
    }>;
    workflow_form_submissions: Array<{
      workflow_id: string;
      form_submission_id: string;
    }>;
    task_events: Array<{ task_id: string; event_id: string }>;
  };
};

export const WorkflowTaskAdapter: WorkflowTaskAdapterType = {
  /**
   * Get the complete list of Workflow Task
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_tasks/search_workflow_tasks
   */
  getList: async ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
    referenceLocation,
    filters,
  }) =>
    fetchAndCollate(
      async ({ page, pageSize }) => {
        const formattedFilters = getFilterObject(filters);
        const resp = await restAPI.nodeAPI.POST<
          GetListResponseType<WorkflowTaskType> | ResponseErrorType
        >({
          endpoint: `/workflow_tasks/search`,
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            group_by: groupBy,
            ...(referenceLocation
              ? { reference_location: referenceLocation }
              : {}),
            filter: formattedFilters,
          },
        });

        if (isResponseErrorType(resp)) {
          console.error(
            "API returned an error for /workflow_tasks/search",
            resp
          );
          throw new HTTPResponseError(
            "API returned an error /workflow_tasks/search"
          );
        }

        return resp;
      },
      page,
      pageSize
    ),
  getListWithRelations: async ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    referenceLocation,
    filters,
  }) => {
    const formattedFilters = getFilterObject(filters);
    if (formattedFilters?.scope?.$like) {
      formattedFilters.scope = formattedFilters.scope.$like;
    }
    const resp = await restAPI.nodeAPI.POST<
      GetListResponseTypeOfWorkflowTasksWithRelations | ResponseErrorType
    >({
      endpoint: `/workflow_tasks/search_with_relations`,
      body: {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
        ...(referenceLocation ? { reference_location: referenceLocation } : {}),
        filter: formattedFilters,
      },
    });

    if (isResponseErrorType(resp)) {
      console.error(
        "API returned an error for /workflow_tasks/search_with_relations",
        resp
      );
      throw new HTTPResponseError(
        "API returned an error /workflow_tasks/search_with_relations"
      );
    }

    return resp;
  },
  savedFilters: SavedFiltersAdapter({ endpoint: "/workflow_tasks/filters" }),
  actions: {
    getList: async ({ page, pageSize, sortBy, sortDirection, filters }) =>
      fetchAndCollate(
        async ({ page, pageSize }) => {
          const formattedFilters = getFilterObject(filters);
          const resp = await restAPI.nodeAPI.POST<
            GetListResponseType<WorkflowTaskType> | ResponseErrorType
          >({
            endpoint: `/workflow_tasks/steps/search`,
            body: {
              page,
              page_size: pageSize,
              sort_by: sortBy,
              sort_direction: sortDirection,
              filter: formattedFilters,
            },
          });

          if (isResponseErrorType(resp)) {
            console.error(
              "API returned an error for /workflow_tasks/steps/search",
              resp
            );
            throw new HTTPResponseError(
              "API returned an error /workflow_tasks/steps/search"
            );
          }

          return resp;
        },
        page,
        pageSize
      ),
  },
  getOne: async ({ id }) => {
    const resp = await restAPI.nodeAPI.GET<WorkflowTaskType>({
      endpoint: `/workflow_tasks/${id}`,
    });

    return {
      data: resp,
    };
  },
  getMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),

  /**
   * Update a workflow task
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_tasks/put_workflow_task
   */
  updateOne: async ({
    id,
    data: {
      status,
      choice,
      assignee_user,
      assignee_group,
      dataset_submission_ids,
    },
  }) => {
    const resp = await restAPI.nodeAPI.PUT<WorkflowTaskType>({
      endpoint: `/workflow_tasks/${id}`,
      body: {
        ...(status ? { status } : {}),
        ...(assignee_user !== undefined ? { assignee_user } : {}),
        ...(assignee_group !== undefined ? { assignee_group } : {}),
        ...(choice !== undefined ? { choice } : {}),
        ...(dataset_submission_ids !== undefined
          ? { dataset_submission_ids }
          : {}),
      },
    });

    return {
      data: resp,
    };
  },
  updateMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  createOne: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  createMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  deleteOne: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  deleteMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  formSubmission: {
    /**
     * relate a form submission to a workflow task
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_tasks/add_workflow_task_form
     */
    updateOne: ({ id, meta }: { id: string; meta?: { taskId: string } }) => {
      return restAPI.nodeAPI.PUT<ResponseSuccessType | ResponseErrorType>({
        endpoint: `/workflow_tasks/${meta?.taskId}/form_submission/${id}`,
      });
    },
    /**
     * remove a form submission relationship from a workflow task
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_tasks/delete_workflow_task_form
     */
    deleteOne: ({ id, meta }: { id: string; meta?: { taskId: string } }) => {
      return restAPI.nodeAPI.DELETE<ResponseSuccessType | ResponseErrorType>({
        endpoint: `/workflow_tasks/${meta?.taskId}/form_submission/${id}`,
      });
    },
  },
};

export interface WorkflowTaskAdapterType
  extends ResourceAdapterType<WorkflowTaskType> {
  savedFilters: SavedFiltersAdapterType<
    typeof WorkflowTaskGetListRequestSchema.shape.filters
  >;
  getList: (
    params: WorkflowTaskGetListRequestType
  ) => Promise<GetListResponseType<WorkflowTaskType>>;
  getListWithRelations: (
    params: {
      filters?: Pick<WorkflowTaskGetListRequestType, "filters"> & {
        scope?: "my-tasks";
      };
    } & Omit<WorkflowTaskGetListRequestType, "filters">
  ) => Promise<GetListResponseTypeOfWorkflowTasksWithRelations>;
  updateOne: (
    params: UpdateOneRequestType<
      Partial<WorkflowTaskType> & {
        choice?: string;
        dataset_submission_ids?: string[];
      }
    >
  ) => Promise<UpdateOneResponseType<WorkflowTaskType>>;
  actions: {
    getList: (
      params: GetListRequestType<
        FilterObjectType<
          Omit<
            WorkflowTaskType,
            "step_id" | "emission_event" | "company_id" | "form_submission"
          >
        >
      >
    ) => Promise<GetListResponseType<WorkflowTaskType>>;
  };
  formSubmission: Pick<ResourceAdapterType<void>, "deleteOne"> & {
    updateOne: (
      params: UpdateOneRequestType<
        FormSubmissionType,
        {
          taskId: string;
        }
      >
    ) => Promise<ResponseSuccessType | ResponseErrorType>;
    deleteOne: (
      params: DeleteOneRequestType<
        FormSubmissionType,
        {
          taskId: string;
        }
      >
    ) => Promise<ResponseSuccessType | ResponseErrorType>;
  };
}
